"use client"

import { useRef, useEffect } from "react"
import { HighlightText } from "@/components/highlight-text"
import gsap from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"

gsap.registerPlugin(ScrollTrigger)

export function PrinciplesSection() {
  const sectionRef = useRef<HTMLElement>(null)
  const headerRef = useRef<HTMLDivElement>(null)
  const principlesRef = useRef<HTMLDivElement>(null)

  const principles = [
    {
      number: "01",
      titleParts: [
        { text: "SECURITY", highlight: true },
        { text: " BY DESIGN", highlight: false },
      ],
      description: "Every system we build is architected with security-first principles. No shortcuts.",
      align: "left",
    },
    {
      number: "02",
      titleParts: [
        { text: "ENTERPRISE", highlight: true },
        { text: " ARCHITECTURE", highlight: false },
      ],
      description: "Built to scale from early-stage startups to Fortune 500 companies with zero-trust security.",
      align: "right",
    },
    {
      number: "03",
      titleParts: [
        { text: "DEVSECOPS", highlight: true },
        { text: " CULTURE", highlight: false },
      ],
      description: "Security is integrated into every stage of development, not bolted on after.",
      align: "left",
    },
    {
      number: "04",
      titleParts: [
        { text: "RESEARCH", highlight: true },
        { text: " DRIVEN", highlight: false },
      ],
      description: "Active in vulnerability research, threat intelligence, and security advisories.",
      align: "right",
    },
    {
      number: "05",
      titleParts: [
        { text: "TRANSPARENT", highlight: true },
        { text: " COMMUNICATION", highlight: false },
      ],
      description: "Continuous monitoring and real-time visibility into security posture.",
      align: "left",
    },
    {
      number: "06",
      titleParts: [
        { text: "24/7", highlight: true },
        { text: " SUPPORT", highlight: false },
      ],
      description: "Always-on incident response and security operations.",
      align: "right",
    },
    {
      number: "07",
      titleParts: [
        { text: "CERTIFIED", highlight: true },
        { text: " ENGINEERS", highlight: false },
      ],
      description: "Industry-recognized experts with proven offensive and defensive capabilities.",
      align: "left",
    },
    {
      number: "08",
      titleParts: [
        { text: "CONTINUOUS", highlight: true },
        { text: " MONITORING", highlight: false },
      ],
      description: "24/7 security monitoring with automated threat detection and response.",
      align: "right",
    },
  ]

  useEffect(() => {
    if (!sectionRef.current || !headerRef.current || !principlesRef.current) return

    const ctx = gsap.context(() => {
      // Header slide in
      gsap.from(headerRef.current, {
        x: -60,
        opacity: 0,
        duration: 1,
        ease: "power3.out",
        scrollTrigger: {
          trigger: headerRef.current,
          start: "top 85%",
          toggleActions: "play none none reverse",
        },
      })

      // Each principle slides in from its aligned side
      const articles = principlesRef.current?.querySelectorAll("article")
      articles?.forEach((article, index) => {
        const isRight = principles[index].align === "right"
        gsap.from(article, {
          x: isRight ? 80 : -80,
          opacity: 0,
          duration: 1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: article,
            start: "top 85%",
            toggleActions: "play none none reverse",
          },
        })
      })
    }, sectionRef)

    return () => ctx.revert()
  }, [])

  return (
    <section ref={sectionRef} id="principles" className="relative py-32 pl-6 md:pl-28 pr-6 md:pr-12">
      {/* Section header */}
      <div ref={headerRef} className="mb-24">
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-accent">03 / Core Principles</span>
        <h2 className="mt-4 font-[var(--font-bebas)] text-5xl md:text-7xl tracking-tight">OUR APPROACH</h2>
      </div>

      {/* Staggered principles */}
      <div ref={principlesRef} className="space-y-24 md:space-y-32">
        {principles.map((principle, index) => (
    <article
      key={index}
      className={`flex flex-col ${
        principle.align === "right" ? "items-end text-right" : "items-start text-left"
      }`}
    >
      {/* Annotation label */}
      <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-4">
        {principle.number} / {principle.titleParts[0].text.split(" ")[0]}
      </span>

      <h3 className="font-[var(--font-bebas)] text-4xl md:text-6xl lg:text-8xl tracking-tight leading-none">
        {principle.titleParts.map((part, i) =>
          part.highlight ? (
            <HighlightText key={i} parallaxSpeed={0.6}>
              {part.text}
            </HighlightText>
          ) : (
            <span key={i}>{part.text}</span>
          ),
        )}
      </h3>

      {/* Description */}
      <p className="mt-6 max-w-md font-sans text-sm text-muted-foreground leading-relaxed">
        {principle.description}
      </p>

      {/* Decorative line */}
      <div className={`mt-8 h-[1px] bg-border w-24 md:w-48 ${principle.align === "right" ? "mr-0" : "ml-0"}`} />
    </article>
  ))}
      </div>
    </section>
  )
}
